import { useState, useEffect, useMemo, type FormEvent, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, Loader2, Plus, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/contexts/AuthContext'
import { useOrg } from '@/contexts/OrgContext'
import { invokeWithFreshAuth } from '@/lib/edge-functions'
import { toast } from 'sonner'

// PSP BC form — CREATE. Surfaces every input the psp_bc slides read and maps
// 1:1 with PspInputs in supabase/functions/_shared/psp-bc-math.ts. Argument is
// BUILD-VS-BUY: the four PSP-economics fields are hard-required (never invented);
// the APM lever is 0 when there are no real gaps (never a benchmark). Each field
// carries help text explaining what it is + which formula it feeds, and a live
// preview shows the three value buckets as the AE types (server recomputes).

interface Props {
  open: boolean
  onClose: () => void
  onSaved?: (slug: string, url: string) => void
}

const DEFAULTS = {
  country: 'MX',
  currency: 'USD',
  deckDate: '2026',
  // Build-vs-buy benchmarks (match the edge-fn soft defaults).
  buildTeam: '8',
  buildMonths: '21',
  loadedCost: '12960',
  yunoFee: '420000',
  integWeeks: '8',
  apmUplift: '4',
}

const COUNTRY_OPTIONS = [
  { value: 'MX', label: 'México' },
  { value: 'BR', label: 'Brasil' },
  { value: 'CO', label: 'Colombia' },
  { value: 'AR', label: 'Argentina' },
  { value: 'CL', label: 'Chile' },
  { value: 'PE', label: 'Perú' },
  { value: 'US', label: 'United States' },
  { value: 'ES', label: 'España' },
]
const CURRENCY_OPTIONS = ['USD', 'MXN', 'BRL', 'ARS', 'COP', 'PEN', 'CLP', 'EUR', 'GBP']

// Blank → omit key (edge fn treats absent as "use benchmark" for soft inputs, or
// "lever = 0" for optional levers). Never coerces blank to 0.
function putNum(payload: Record<string, unknown>, key: string, raw: string) {
  const s = raw.trim()
  if (s === '') return
  const n = Number(s)
  if (Number.isFinite(n)) payload[key] = n
}
function num(raw: string): number | null {
  const s = raw.trim()
  if (s === '') return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

function Field({ label, help, children }: { label: string; help: ReactNode; children: ReactNode }) {
  return (
    <div>
      <Label className="text-[13px]">{label}</Label>
      {children}
      <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{help}</p>
    </div>
  )
}

function SectionTitle({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div className="mb-3 border-b pb-1.5">
      <p className="text-sm font-semibold">{children}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  )
}

const fmtUsd = (v: number) =>
  v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const fmtCompact = (v: number) => {
  const a = Math.abs(v)
  if (a >= 1e9) return `$${(v / 1e9).toFixed(2)}B`
  if (a >= 1e6) return `$${(v / 1e6).toFixed(2)}M`
  return fmtUsd(v)
}

export function NewPspBcForm({ open, onClose, onSaved }: Props) {
  const { user } = useAuth()
  const { org } = useOrg()
  const qc = useQueryClient()

  // Identity
  const [clientName, setClientName] = useState('')
  const [createdByEmail, setCreatedByEmail] = useState('')
  const [country, setCountry] = useState(DEFAULTS.country)
  const [currency, setCurrency] = useState(DEFAULTS.currency)
  const [deckDate, setDeckDate] = useState(DEFAULTS.deckDate)
  const [clientLogo, setClientLogo] = useState('')

  // PSP economics (HARD-required)
  const [tpvMonthly, setTpvMonthly] = useState('')
  const [takeRate, setTakeRate] = useState('')
  const [avgTicket, setAvgTicket] = useState('')
  const [currentAr, setCurrentAr] = useState('')

  // Build-vs-buy (soft, benchmarked)
  const [buildTeam, setBuildTeam] = useState(DEFAULTS.buildTeam)
  const [buildMonths, setBuildMonths] = useState(DEFAULTS.buildMonths)
  const [loadedCost, setLoadedCost] = useState(DEFAULTS.loadedCost)
  const [yunoFee, setYunoFee] = useState(DEFAULTS.yunoFee)
  const [integWeeks, setIntegWeeks] = useState(DEFAULTS.integWeeks)

  // Smart routing (optional)
  const [targetAr, setTargetAr] = useState('')
  const [currentProc, setCurrentProc] = useState('')
  const [targetProc, setTargetProc] = useState('')

  // New APMs (optional, anti-fabrication)
  const [apmTpv, setApmTpv] = useState('')
  const [apmUplift, setApmUplift] = useState(DEFAULTS.apmUplift)
  const [apmGaps, setApmGaps] = useState<Array<{ method: string; country: string }>>([
    { method: 'PIX', country: 'BR' },
    { method: 'SPEI', country: 'MX' },
  ])

  // Direct integrations (optional)
  const [curCostTx, setCurCostTx] = useState('')
  const [tgtCostTx, setTgtCostTx] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setClientName('')
    setCountry(DEFAULTS.country)
    setCurrency(DEFAULTS.currency)
    setDeckDate(DEFAULTS.deckDate)
    setClientLogo('')
    setTpvMonthly('')
    setTakeRate('')
    setAvgTicket('')
    setCurrentAr('')
    setBuildTeam(DEFAULTS.buildTeam)
    setBuildMonths(DEFAULTS.buildMonths)
    setLoadedCost(DEFAULTS.loadedCost)
    setYunoFee(DEFAULTS.yunoFee)
    setIntegWeeks(DEFAULTS.integWeeks)
    setTargetAr('')
    setCurrentProc('')
    setTargetProc('')
    setApmTpv('')
    setApmUplift(DEFAULTS.apmUplift)
    setApmGaps([{ method: 'PIX', country: 'BR' }, { method: 'SPEI', country: 'MX' }])
    setCurCostTx('')
    setTgtCostTx('')
    setError(null)
    if (user?.email) setCreatedByEmail(user.email)
  }, [open, user?.email])

  // ── Live preview of the three value buckets. Mirrors psp-bc-math.ts; the
  //    server recomputes on submit (source of truth). Shown only when the four
  //    hard-required fields are present, so nothing is fabricated. ──
  const preview = useMemo(() => {
    const tpv = num(tpvMonthly)
    const take = num(takeRate)
    const ticket = num(avgTicket)
    const ar = num(currentAr)
    if (!tpv || tpv <= 0 || !take || take <= 0 || !ticket || ticket <= 0 || !ar || ar <= 0) return null

    const team = num(buildTeam) ?? 8
    const months = num(buildMonths) ?? 21
    const loaded = num(loadedCost) ?? 12960
    const fee = num(yunoFee) ?? 420000
    const weeks = num(integWeeks) ?? 8

    const takeFrac = take / 100
    const arFrac = ar / 100
    const monthlyNetRev = tpv * takeFrac
    const annualTpv = tpv * 12
    const annualAttempts = arFrac > 0 ? (tpv / ticket) * 12 / arFrac : 0

    // (d) build-vs-buy
    const buildCost = team * loaded * months
    const avoided = buildCost - fee
    // (e) time-to-market
    const monthsPulled = Math.max(0, months - weeks / 4.33)
    const pulled = monthsPulled * monthlyNetRev
    // (a) routing
    const tAr = num(targetAr)
    const dAr = tAr !== null ? Math.max(0, tAr / 100 - arFrac) : 0
    const routingRev = annualAttempts * dAr * ticket * takeFrac
    const cProc = num(currentProc)
    const tProc = num(targetProc)
    const procSav = cProc !== null && tProc !== null && cProc > tProc ? annualTpv * ((cProc - tProc) / 100) : 0
    // (b) APMs (0 if no market)
    const aTpv = num(apmTpv)
    const aUpl = num(apmUplift) ?? 4
    const apmRev = aTpv !== null && aTpv > 0 ? aTpv * (aUpl / 100) * takeFrac : 0
    // (c) direct integrations
    const cTx = num(curCostTx)
    const tTx = num(tgtCostTx)
    const directSav = cTx !== null && tTx !== null && cTx > tTx ? annualAttempts * (cTx - tTx) : 0

    const recurring = routingRev + procSav + apmRev + directSav
    return {
      oneTime: avoided,
      pulled,
      recurring,
      buildMult: fee > 0 ? buildCost / fee : 0,
      routingRev, procSav, apmRev, directSav,
    }
  }, [tpvMonthly, takeRate, avgTicket, currentAr, buildTeam, buildMonths, loadedCost, yunoFee, integWeeks, targetAr, currentProc, targetProc, apmTpv, apmUplift, curCostTx, tgtCostTx])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!clientName.trim()) { setError('El nombre del PSP es requerido'); return }
    if (!createdByEmail.trim()) { setError('Gmail conectado (AE) es requerido para crear'); return }
    if (!(Number(tpvMonthly) > 0)) { setError('TPV mensual del PSP es requerido (> 0)'); return }
    if (!(Number(takeRate) > 0)) { setError('Take rate NETO del PSP es requerido (> 0, ~1.2% — no el 15% de Yuno)'); return }
    if (!(Number(avgTicket) > 0)) { setError('Ticket promedio es requerido (> 0)'); return }
    const arN = Number(currentAr)
    if (!(arN > 0 && arN <= 100)) { setError('Approval rate actual es requerido (0 < AR ≤ 100)'); return }

    const inputs: Record<string, unknown> = {
      psp_tpv_monthly_usd: Number(tpvMonthly),
      psp_take_rate_pct: Number(takeRate),
      avg_ticket_usd: Number(avgTicket),
      current_ar_pct: arN,
    }
    putNum(inputs, 'build_team_size', buildTeam)
    putNum(inputs, 'build_months', buildMonths)
    putNum(inputs, 'loaded_cost_per_eng_month_usd', loadedCost)
    putNum(inputs, 'yuno_annual_fee_usd', yunoFee)
    putNum(inputs, 'yuno_integration_weeks', integWeeks)
    putNum(inputs, 'target_ar_pct', targetAr)
    putNum(inputs, 'current_proc_cost_pct', currentProc)
    putNum(inputs, 'target_proc_cost_pct', targetProc)
    putNum(inputs, 'apm_new_market_tpv_annual_usd', apmTpv)
    putNum(inputs, 'apm_uplift_pct', apmUplift)
    putNum(inputs, 'current_cost_per_tx_usd', curCostTx)
    putNum(inputs, 'target_cost_per_tx_usd', tgtCostTx)
    const cleanGaps = apmGaps
      .map((g) => ({ method: g.method.trim(), country: g.country.trim().toUpperCase() }))
      .filter((g) => g.method || g.country)
    if (cleanGaps.length) inputs.apm_gaps = cleanGaps
    if (currency.trim()) inputs.currency = currency.trim().toUpperCase()

    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        createdByEmail: createdByEmail.trim(),
        org_id: org?.id,
        client_name: clientName.trim(),
        country,
        language: 'es',
        currency,
        deck_date: deckDate.trim() || null,
        client_logo: clientLogo.trim() || null,
        inputs,
      }
      const { data, error: invokeErr } = await invokeWithFreshAuth<{
        slug?: string; url?: string; warnings?: Record<string, boolean>
      }>('psp-bc-generate', { body, timeoutMs: 60_000 })

      if (invokeErr) {
        const ctx = (invokeErr as { context?: Response }).context
        if (ctx && typeof ctx.json === 'function') {
          try {
            const b = await ctx.clone().json() as { error?: string; reason?: string } | null
            if (b?.error) throw new Error(b.reason ? `${b.error} (${b.reason})` : b.error)
          } catch (parseErr) {
            if (parseErr instanceof Error && parseErr.message && parseErr.message !== 'Unexpected end of JSON input') throw parseErr
          }
        }
        throw invokeErr
      }

      const result = data
      if (!result?.slug) throw new Error('Respuesta inválida del servidor (sin slug)')
      const url = result.url || `https://chief.yuno.tools/psp/${result.slug}`
      const warnings = result.warnings || {}
      const warnKeys = Object.keys(warnings).filter((k) => warnings[k])

      qc.invalidateQueries({ queryKey: ['psp_bc'] })
      toast.success(`PSP BC creado · ${clientName.trim()}`, {
        description: url,
        action: { label: 'Abrir', onClick: () => window.open(url, '_blank', 'noopener') },
      })
      if (warnKeys.includes('take_rate_high')) {
        toast.warning('¿Take rate correcto?', { description: 'Usaste un take > 5%. El margen NETO del PSP suele ser ~1–2%; no uses el 15% de Yuno.' })
      }
      if (warnKeys.filter((k) => k !== 'take_rate_high').length) {
        toast.warning('Usé benchmarks por defecto', { description: `Confirmá: ${warnKeys.filter((k) => k !== 'take_rate_high').join(', ')}` })
      }

      onSaved?.(result.slug, url)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error generando el PSP BC')
    } finally {
      setSubmitting(false)
    }
  }

  const updateGap = (idx: number, key: 'method' | 'country', val: string) =>
    setApmGaps((prev) => prev.map((g, i) => (i === idx ? { ...g, [key]: val } : g)))
  const addGap = () => setApmGaps((prev) => [...prev, { method: '', country: '' }])
  const removeGap = (idx: number) => setApmGaps((prev) => prev.filter((_, i) => i !== idx))

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Nuevo PSP BC — Build vs Buy
          </DialogTitle>
          <DialogDescription>
            Caso de negocio whitelabel para un PSP / adquirente / gateway. Argumento: lo que costaría CONSTRUIR la capa de pagos in-house vs. comprar Yuno. Los 4 campos de economía del PSP no se inventan; el lever de APMs es 0 si no hay gaps reales.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ── Identity ── */}
          <div>
            <SectionTitle>Identidad</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Field label="PSP / Adquirente / Gateway *" help="El cliente. Aparece en la portada como “[PSP] × Yuno” y en el diagrama whitelabel.">
                  <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Kushki" autoFocus />
                </Field>
              </div>
              <div className="col-span-2">
                <Field label="Gmail conectado (AE) *" help="Tu Gmail conectado en Chief. Resuelve tu org y te asigna como creador del deck.">
                  <Input type="email" value={createdByEmail} onChange={(e) => setCreatedByEmail(e.target.value)} placeholder="tu@y.uno" />
                </Field>
              </div>
              <Field label="País" help="Mercado principal. Se muestra en la portada.">
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Moneda (display)" help="La matemática es en USD; la moneda solo cambia el símbolo mostrado.">
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Fecha del deck" help="Texto libre. Portada y footer.">
                <Input value={deckDate} onChange={(e) => setDeckDate(e.target.value)} placeholder="2026" />
              </Field>
              <Field label="Logo del PSP (URL, opcional)" help="URL absoluta del logo, opcional.">
                <Input value={clientLogo} onChange={(e) => setClientLogo(e.target.value)} placeholder="https://…" />
              </Field>
            </div>
          </div>

          {/* ── PSP economics (HARD) ── */}
          <div>
            <SectionTitle sub="No se inventan — el servidor devuelve 422 si faltan. Son la base de todos los cálculos.">Economía del PSP · requerido</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <Field label="TPV mensual del PSP (USD) *" help="Volumen que el PSP procesa al mes. Alimenta: TPV anual, tx/mes (TPV ÷ ticket) y el margen mensual neto (TPV × take).">
                <Input type="number" min="0" value={tpvMonthly} onChange={(e) => setTpvMonthly(e.target.value)} placeholder="80000000" />
              </Field>
              <Field label="Take rate NETO del PSP (%) *" help="El margen NETO del PSP sobre TPV (~1.2%). ⚠️ NO es el 15% de Yuno — confundirlos infla todo 10×. Alimenta margen mensual y revenue de routing/APMs.">
                <Input type="number" min="0" step="0.01" value={takeRate} onChange={(e) => setTakeRate(e.target.value)} placeholder="1.2" />
              </Field>
              <Field label="Ticket promedio (USD) *" help="Valor promedio por transacción. Alimenta: tx/mes = TPV ÷ ticket, y el TPV incremental por ↑AR.">
                <Input type="number" min="0" step="0.01" value={avgTicket} onChange={(e) => setAvgTicket(e.target.value)} placeholder="40" />
              </Field>
              <Field label="Approval rate actual (%) *" help="AR actual del PSP. Alimenta intentos/año = (tx/mes × 12) ÷ AR, base de routing e integraciones directas.">
                <Input type="number" min="0" max="100" step="0.1" value={currentAr} onChange={(e) => setCurrentAr(e.target.value)} placeholder="84" />
              </Field>
            </div>
          </div>

          {/* ── Build-vs-buy (ANCHOR) ── */}
          <div>
            <SectionTitle sub="El ancla del deck. En blanco → usa el benchmark (con aviso).">Build-vs-Buy · ancla</SectionTitle>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Tamaño del equipo (ing.)" help="Ingenieros senior para construir la capa. Rango 6–10. build_cost = equipo × costo/mes × meses.">
                <Input type="number" min="1" value={buildTeam} onChange={(e) => setBuildTeam(e.target.value)} placeholder="8" />
              </Field>
              <Field label="Meses a paridad" help="Meses para alcanzar paridad in-house. Rango 18–24. Alimenta build_cost y meses adelantados.">
                <Input type="number" min="1" value={buildMonths} onChange={(e) => setBuildMonths(e.target.value)} placeholder="21" />
              </Field>
              <Field label="Costo cargado / ing. / mes (USD)" help="Costo totalmente cargado por ingeniero/mes. Default 12,960.">
                <Input type="number" min="0" value={loadedCost} onChange={(e) => setLoadedCost(e.target.value)} placeholder="12960" />
              </Field>
              <Field label="Fee anual Yuno (USD)" help="El “comprar”. buy_cost. R&D evitado = build_cost − fee. También el denominador del ROI.">
                <Input type="number" min="0" value={yunoFee} onChange={(e) => setYunoFee(e.target.value)} placeholder="420000" />
              </Field>
              <Field label="Semanas para integrar Yuno" help="Time-to-market con Yuno. meses_adelantados = meses − semanas ÷ 4.33.">
                <Input type="number" min="0" value={integWeeks} onChange={(e) => setIntegWeeks(e.target.value)} placeholder="8" />
              </Field>
            </div>
          </div>

          {/* ── Smart routing (optional) ── */}
          <div>
            <SectionTitle sub="Opcional — en blanco ⇒ esa porción del lever = 0.">Smart routing · recurrente</SectionTitle>
            <div className="grid grid-cols-3 gap-3">
              <Field label="AR objetivo con Yuno (%)" help="Approval rate meta. Δ = objetivo − actual. Revenue = intentos × Δ × ticket × take. En blanco ⇒ 0.">
                <Input type="number" min="0" max="100" step="0.1" value={targetAr} onChange={(e) => setTargetAr(e.target.value)} placeholder="87" />
              </Field>
              <Field label="Costo de proc. actual (%)" help="Costo de procesamiento actual sobre TPV. Con el objetivo forma el ahorro. En blanco ⇒ 0.">
                <Input type="number" min="0" step="0.01" value={currentProc} onChange={(e) => setCurrentProc(e.target.value)} placeholder="0.55" />
              </Field>
              <Field label="Costo de proc. objetivo (%)" help="Costo con Yuno. Ahorro = TPV anual × (actual − objetivo). En blanco ⇒ 0.">
                <Input type="number" min="0" step="0.01" value={targetProc} onChange={(e) => setTargetProc(e.target.value)} placeholder="0.48" />
              </Field>
            </div>
          </div>

          {/* ── New APMs (optional, anti-fabrication) ── */}
          <div>
            <SectionTitle sub="Anti-fabricación: si el PSP no tiene gaps reales, dejá el TPV en blanco → lever = 0. Nunca un benchmark.">Nuevos APMs · recurrente</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Mercado nuevo por APMs (USD/año)" help="TPV anual del mercado que hoy NO capturas por falta de métodos. En blanco ⇒ lever = 0. uplift_tpv = mercado × uplift%.">
                <Input type="number" min="0" value={apmTpv} onChange={(e) => setApmTpv(e.target.value)} placeholder="120000000 (o vacío si no hay gaps)" />
              </Field>
              <Field label="Uplift de conversión (%)" help="Conversión incremental sobre el mercado nuevo. revenue = uplift_tpv × take. Default 4%.">
                <Input type="number" min="0" step="0.1" value={apmUplift} onChange={(e) => setApmUplift(e.target.value)} placeholder="4" />
              </Field>
            </div>
            <div className="mt-3">
              <Label className="text-[13px]">Gaps de método (cualitativo, on-slide)</Label>
              <p className="mb-2 mt-1 text-[11px] leading-snug text-muted-foreground">Métodos que los merchants piden por mercado y el PSP hoy no ofrece. Solo narrativa: no alimentan la matemática.</p>
              <div className="space-y-2">
                {apmGaps.map((g, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input placeholder="Método (PIX)" value={g.method} onChange={(e) => updateGap(idx, 'method', e.target.value)} />
                    <Input placeholder="País (BR)" value={g.country} onChange={(e) => updateGap(idx, 'country', e.target.value)} className="max-w-[120px]" />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeGap(idx)}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addGap}><Plus className="mr-1 h-4 w-4" /> Agregar gap</Button>
              </div>
            </div>
          </div>

          {/* ── Direct integrations (optional) ── */}
          <div>
            <SectionTitle sub="Opcional — en blanco ⇒ lever = 0.">Integraciones directas · recurrente</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Costo por tx actual (USD)" help="Costo fijo por transacción hoy (con intermediarios). Ahorro = intentos/año × (actual − objetivo).">
                <Input type="number" min="0" step="0.001" value={curCostTx} onChange={(e) => setCurCostTx(e.target.value)} placeholder="0.045" />
              </Field>
              <Field label="Costo por tx objetivo (USD)" help="Costo con integraciones directas de Yuno. En blanco ⇒ lever = 0.">
                <Input type="number" min="0" step="0.001" value={tgtCostTx} onChange={(e) => setTgtCostTx(e.target.value)} placeholder="0.030" />
              </Field>
            </div>
          </div>

          {/* ── Live preview ── */}
          <div className="rounded-lg border bg-muted/40 p-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Vista previa · estimación previa (el número final lo calcula el servidor)
            </p>
            {preview ? (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">One-time · R&amp;D evitado</p>
                    <p className="text-2xl font-light tabular-nums">{fmtCompact(preview.oneTime)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Adelantado · margen</p>
                    <p className="text-2xl font-light tabular-nums text-primary">{fmtCompact(preview.pulled)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Recurrente / año</p>
                    <p className="text-2xl font-light tabular-nums">{fmtCompact(preview.recurring)}</p>
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground tabular-nums">
                  Recurrente = routing {fmtCompact(preview.routingRev + preview.procSav)} + APMs {fmtCompact(preview.apmRev)} + integraciones {fmtCompact(preview.directSav)} · por cada $1 de fee evitas ${preview.buildMult.toFixed(1)} de construcción. Los tres buckets no se suman en un solo número.
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Completá los 4 campos de economía del PSP para ver la estimación.</p>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>Cancelar</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Generar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
